import { HStack, chakra } from '@chakra-ui/react';
import React from 'react';

import type { NavGroupItem } from 'types/client/navigation';

import { Link } from 'toolkit/chakra/link';
import IconSvg from 'ui/shared/IconSvg';

import LightningLabel from '../LightningLabel';
import { checkRouteHighlight } from '../utils';
import NavLink from './NavLink';

interface Props {
  item: NavGroupItem;
  isOpen?: boolean;
  onClick?: () => void;
  onHoverOpen?: () => void;
  onHoverClose?: () => void;
  isTouch?: boolean;
}

const NavLinkGroup = ({
  item,
  isOpen,
  onClick,
  onHoverOpen,
  onHoverClose,
  isTouch,
}: Props) => {
  const isHighlighted = checkRouteHighlight(item.subItems);
  const hasGroups = item.subItems.some((sub) => Array.isArray(sub));
  const ref = React.useRef<HTMLLIElement>(null);

  // Close on outside click (touch)
  React.useEffect(() => {
    if (!isTouch || !isOpen) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClick?.(); // toggles closed
      }
    };
    document.addEventListener('touchstart', handleOutside);
    return () => document.removeEventListener('touchstart', handleOutside);
  }, [ isTouch, isOpen, onClick ]);

  const content = hasGroups ? (
    <HStack alignItems="flex-start">
      { item.subItems.map((subItem, index) => {
        if (!Array.isArray(subItem)) {
          return <NavLink key={ subItem.text } item={ subItem }/>;
        }
        return (
          <chakra.ul key={ index } display="flex" flexDir="column" rowGap={ 1 }>
            { subItem.map((navItem) => (
              <NavLink key={ navItem.text } item={ navItem }/>
            )) }
          </chakra.ul>
        );
      }) }
    </HStack>
  ) : (
    <chakra.ul display="flex" flexDir="column" rowGap={ 1 }>
      { item.subItems.map((subItem) => {
        if (Array.isArray(subItem)) return null;
        return <NavLink key={ subItem.text } item={ subItem }/>;
      }) }
    </chakra.ul>
  );

  return (
    <chakra.li
      ref={ ref }
      listStyleType="none"
      position="relative"
      onMouseEnter={ !isTouch ? onHoverOpen : undefined }
      onMouseLeave={ !isTouch ? onHoverClose : undefined }
      className="li-menu-items"
    >
      <Link
        display="flex"
        alignItems="center"
        px={ 2 }
        py={ 1.5 }
        fontWeight={ 500 }
        borderRadius="base"
        color="inherit"
        _hover={{
          color: 'link.navigation.fg.hover',
        }}
        transition="color 0.16s ease-out"
        data-active={ isOpen ? true : undefined }
        // ↓ handle touch tap on the trigger itself
        onClick={
          isTouch ?
            (e) => {
              e.preventDefault();
              onClick?.();
            } :
            undefined
        }
      >
        { item.text }
        { isHighlighted && <LightningLabel iconColor="inherit" ml="2px"/> }
        <IconSvg
          name="arrows/east-mini"
          boxSize={ 5 }
          transform={ isOpen ? 'rotate(90deg)' : 'rotate(-90deg)' }
          ml={ 1 }
          transition="transform 0.15s"
        />
      </Link>

      { isOpen && (
        <chakra.div
          position="absolute"
          top="100%"
          left={ 0 }
          zIndex={ 1000 }
          bg="bg.primary"
          borderWidth="1px"
          borderColor="border.divider"
          boxShadow="0 8px 24px rgba(0,0,0,0.15)"
          borderRadius="md"
          p={ 3 }
          minW="220px"
        >
          { content }
        </chakra.div>
      ) }
    </chakra.li>
  );
};

export default React.memo(NavLinkGroup);
